import { arrayMove } from '@dnd-kit/sortable';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import type { Item, Pagination } from '@/models';
import { enhanceFetchedAssets } from '@/pages/assets/asset-listing/enhanceFetchedAsset';
import { AssetFilterType, GetAssetFilterTypeOptions } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { initialPagination } from '@/store/forms/Channel/DynamicLodContentForm';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';
import { dateUtils } from '@/utils/dateUtils';

import { AssetDuration } from '../asset-duration';
import FuelActionbar from '../fuel-actionbar';
import FuelAssetSearch from '../FuelAssetSearch/FuelAssetSearch';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';
import DragOverlayRow from '../VNTable/DragOverlayRow';
import VNTable from '../VNTable/VNTable';
import VnTableDndProvider from '../VNTable/VnTableDndProvider';

const ContentClipsTab = () => {
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const [items, setItems] = useState<Item[]>([]);
  const flags = useFlags();
  const { alert } = useAppUtils();

  const { watch, getValues, setValue } = useFormContext();
  const clipGroupContentClips = watch('clipGroupContentClips');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDragItem, setActiveDragItem] = useState<Item | null>(null);

  const vnTableMethods = useForm({
    defaultValues: {
      filters: { searchText: '', status: '', showThumbnail: true },
      selectedRows: [],
      pagination: initialPagination,
      data: [], // Store your table data here
    },
  });

  useEffect(() => {
    init();
  }, [clipGroupContentClips]);

  useEffect(() => {
    init();
    return () => {
      // reset();
      // console.log('cleaned up  container');
    };
  }, []);
  const init = async () => {
    const clips: Item[] = getValues('clipGroupContentClips');
    if (clips && clips.length > 0) {
      const myPagination = _.cloneDeep(initialPagination);
      myPagination.totalItems = clips.length;
      updateClipGroupState(clips);
    }
  };

  const Thumbnail = useCallback(
    (cell) => (
      <FuelAssetThumbnail
        item={cell.row.original}
        permissions={permissions}
        // onClick={(event) => openEditPopup(event, cell.row.original)}
      />
    ),
    [permissions]
  );

  const columnHelper = createColumnHelper<any>();
  const columns = useMemo(
    () => [
      // columnHelper.accessor('handle', {
      //   header: () => '',
      //   size: '25px',
      //   sortable: false,
      //   cell: () => (
      //     <i aria-hidden="true" className="icon material-icons">
      //       reorder
      //     </i>
      //   ),
      // }),
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        header: () => 'Title',
        size: 250,
        meta: {
          sortable: false,
          locked: true, //
          headerTitle: 'Title',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        footer: (props) => props.column.id,
        cell: (cell) => {
          return (
            <FuelAssetTitle
              item={cell.row.original}
              permissions={permissions}
              // onClick={(event) => openEditPopup(event, cell.row.original)}
            />
          );
        },
      }),
      columnHelper.accessor('publicationDate', {
        header: `Publication Date (${ouData.timezoneAbbreviation})`,
        footer: (props) => props.column.id,
        size: 150,
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        footer: (props) => props.column.id,
        size: 130,
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        footer: (props) => props.column.id,
        size: 130,
        meta: {
          sortable: false,
          locked: true, //
          headerTitle: 'Tags',
          // style: {
          //   textAlign: 'left',
          // },
          // contentStyle: {
          //   justifyContent: 'flex-start',
          // },
        },
        cell: (cell) => (
          <div title={cell.getValue()} className="text-ellipses">
            {cell.getValue()}
          </div>
        ),
      }),
      // columnHelper.accessor('data.accessSettings.effectiveAccessLevel', {
      //   header: () => `Access`,
      //   footer: (props) => props.column.id,
      //   width: 130,
      //   sortable: false,
      //   cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
      // }),
      // columnHelper.accessor('embargoDate', {
      //   header: () => `Embargo (${ouData.timezoneAbbreviation})`,
      //   footer: (props) => props.column.id,
      //   width: 130,
      //   sortable: false,
      //   cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      // }),
      // columnHelper.accessor('expireDate', {
      //   header: `Expiry (${ouData.timezoneAbbreviation})`,
      //   footer: (props) => props.column.id,
      //   width: 130,
      //   sortable: false,
      //   cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      // }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        footer: (props) => props.column.id,
        size: 75,
        cell: (cell) => <AssetDuration item={cell.row.original} />,
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        footer: (props) => props.column.id,
        size: 75,
        cell: (cell) => (
          <FuelActionbar
            actions={[
              {
                title: 'Delete',
                className: 'action',
                type: 'delete',
                iconClass: 'icon material-icons',
                iconName: 'delete',
                isDisabled: false,
              },
            ]}
            item={cell.row.original}
            onActionButtonClick={onTableAction}
            context={cell}
          />
        ),
      }),
    ],
    [permissions, ouData]
  );
  const paginationChange = (paginationDetail: Pagination) => {};
  const onTableAction = async (event, type, item, context: any) => {
    if (type === 'delete') {
      const myItems = _.cloneDeep(context.table.options.data); // cloneDeep needed here so react knows the object is different
      const foundIndex = _.findIndex(myItems, (x) => x.id === item.id);
      if (foundIndex > -1) {
        myItems.splice(foundIndex, 1);
        updateClipGroupState(myItems);
      }
    }
  };

  const updateClipGroupState = useCallback(
    (newItems: Item[]) => {
      // 1. Sync React Hook Form (Global State)
      const ids = newItems.map((item) => item.id);
      setValue('clipIds', ids);
      setValue('clipGroup.clipIds', ids);
      setValue('clipGroupContentClips', newItems);

      // 2. Sync VNTable Internal Form (Local Table State)
      vnTableMethods.setValue('data', newItems);

      // 3. Sync Local Component State
      setItems(newItems);

      const myPagination = _.cloneDeep(initialPagination);
      myPagination.totalItems = newItems.length;
      vnTableMethods.setValue('pagination', myPagination);

      // if (onFormChanged) onFormChanged();
    },
    [setValue, vnTableMethods, items]
  );

  const handleDragEnd = ({ active, over }: any) => {
    setActiveId(null);
    setActiveDragItem(null);
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const activeAssetId = String(activeData?.originalId || active.id);
    const overAssetId = String(overData?.originalId || over.id);
    const sourceTable = activeData?.tableId;
    const destTable = overData?.tableId || over.id;

    const currentClips = [...(items || [])];

    // --- CASE 1: COPY FROM SEARCH TO CLIP GROUP ---
    if (sourceTable === 'asset-search-table' && destTable.includes('clipgroup-content-clips')) {
      const movedItem = activeData?.row;

      // Validation: Source Restrictions
      if (movedItem.source === 'ClipGroup' || movedItem.source === 'NestedChannel') {
        alert(`${movedItem.source} assets cannot be used in a Clip Group.`, 'warning');
        return;
      }

      // Validation: Duplicates
      if (currentClips.some((item) => String(item.id) === activeAssetId)) {
        alert('Cannot add a duplicate item', 'warning');
        return;
      }

      // Enhance and Insert
      const enhancedItem = enhanceFetchedAssets([movedItem], permissions, flags, true, [], [], ouData)[0];

      const overIndex = currentClips.findIndex((item) => String(item.id) === overAssetId);
      if (overIndex !== -1) {
        currentClips.splice(overIndex, 0, enhancedItem);
      } else {
        currentClips.push(enhancedItem);
      }

      updateClipGroupState(currentClips);
    }

    // --- CASE 2: REORDER WITHIN CLIP GROUP ---
    else if (sourceTable === 'clipgroup-content-clips' && destTable === 'clipgroup-content-clips') {
      if (activeAssetId !== overAssetId) {
        const oldIndex = currentClips.findIndex((item) => String(item.id) === activeAssetId);
        const newIndex = currentClips.findIndex((item) => String(item.id) === overAssetId);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(currentClips, oldIndex, newIndex);
          updateClipGroupState(reordered);
        }
      }
    }
  };
  const handleDragStart = ({ active }: any) => {
    const id = String(active.id);
    const item = active.data.current?.row;
    const sourceTable = active.data.current?.tableId;
    setActiveId(id);
    if (item) {
      setActiveDragItem(item);
    }
  };

  return (
    <div className="clipgroup-lod-content fullHeight" id="clipgroup-lod-content">
      <div className="page-column-wrapper fullHeight">
        <VnTableDndProvider
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
          dropAnimation={activeId?.includes('asset-search-table') ? null : undefined}
          // Only show overlay if the item is NOT coming from Table A
          overlayContent={activeDragItem ? <DragOverlayRow item={activeDragItem} columns={columns} /> : null}
        >
          <div className="page-left-column">
            <FuelAssetSearch
              excludeAssetFilterTypes={GetAssetFilterTypeOptions([
                AssetFilterType.NestedChannel,
                AssetFilterType.ClipGroup,
                AssetFilterType.Transitions,
                AssetFilterType.Image,
                AssetFilterType.Series,
                AssetFilterType.Season,
                AssetFilterType.Extra,
                AssetFilterType.Trailer,
                AssetFilterType.LiveEvent,
                AssetFilterType.ExternalClip,
                AssetFilterType.Broadcast,
              ])}
              showStatus
            />
          </div>
          <div className="page-right-column fullHeight">
            <div className="clipgroup-content-grid-wrapper fullHeight">
              {/* <FuelTable
                columnHelper={columnHelper}
                data={items || []}
                defaultColumns={columns}
                showRowSelection={false}
                paginationDetail={pagination}
                drop
                drag
                onDrag={onDrag}
              /> */}
              <FormProvider {...vnTableMethods}>
                <VNTable
                  columns={columns}
                  paginationChange={paginationChange}
                  externalDnd
                  dataKey="data"
                  showDropZone
                  sortable={() => {}}
                  tableId="clipgroup-content-clips"
                />
              </FormProvider>
            </div>
          </div>
        </VnTableDndProvider>
      </div>
    </div>
  );
};

export default ContentClipsTab;
